import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";

export default function BookingDialog({
  open,
  onOpenChange,
  tour,
  tours,
  onSubmit,
  onSuccess,
  onError,
}) {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    date: "",
    people: 2,
    tour_id: "",
    tour_title: "",
    comment: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setForm((f) => ({
        ...f,
        tour_id: tour?.id || tours[0].id,
        tour_title: tour?.title || tours[0].title,
      }));
    }
  }, [open, tour, tours]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    if (form.name.trim().length < 2) return "Укажите ваше имя";
    if (form.phone.trim().length < 5) return "Укажите телефон";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return "Проверьте email";
    if (!form.date) return "Выберите дату";
    if (!form.people || form.people < 1) return "Укажите количество человек";
    if (!form.tour_id) return "Выберите экскурсию";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      onError && onError(err);
      return;
    }
    setLoading(true);
    try {
      await onSubmit(form);
      setLoading(false);
      onOpenChange(false);
      onSuccess && onSuccess();
      setForm((f) => ({ ...f, name: "", phone: "", email: "", date: "", comment: "" }));
    } catch (e) {
      setLoading(false);
      const msg =
        e?.response?.data?.detail ||
        "Не удалось отправить заявку. Проверьте соединение и попробуйте ещё раз.";
      onError && onError(msg);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="booking-dialog"
        className="max-w-2xl bg-[#FAF7F2] border-border p-0 overflow-hidden"
      >
        <div className="grid md:grid-cols-5">
          <div className="hidden md:block md:col-span-2 bg-[#1A2035] text-[#FAF7F2] p-8 relative">
            <p className="overline text-[#F5C58C]">Дели с Али</p>
            <h3 className="font-display text-3xl leading-tight mt-4">
              Оставьте заявку — <span className="italic text-[#F5C58C]">и мы всё устроим</span>
            </h3>
            <p className="text-[#FAF7F2]/70 text-sm mt-6 leading-relaxed">
              Ответим в WhatsApp/Telegram в течение 1–2 часов. Никакой
              предоплаты для однодневных экскурсий.
            </p>
            <div className="mt-8 space-y-3 text-sm text-[#FAF7F2]/80">
              <p>· Русский язык</p>
              <p>· Проверенные водители</p>
              <p>· Поддержка 24/7</p>
              <p>· Отмена без штрафа за 24 часа</p>
            </div>
          </div>

          <form className="md:col-span-3 p-6 md:p-8" onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl font-normal">
                Бронирование экскурсии
              </DialogTitle>
              <DialogDescription className="text-[13px] text-muted-foreground">
                Заполните форму — все поля обязательны, кроме комментария.
              </DialogDescription>
            </DialogHeader>

            <div className="mt-6 space-y-5">
              <div>
                <label className="overline text-muted-foreground">Экскурсия</label>
                <Select
                  value={form.tour_id}
                  onValueChange={(v) => {
                    const t = tours.find((x) => x.id === v);
                    set("tour_id", v);
                    set("tour_title", t?.title || "");
                  }}
                >
                  <SelectTrigger
                    data-testid="booking-tour-select"
                    className="mt-2 h-11 rounded-md border-border bg-white"
                  >
                    <SelectValue placeholder="Выберите экскурсию" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {tours.map((t) => (
                      <SelectItem key={t.id} value={t.id} data-testid={`booking-tour-opt-${t.id}`}>
                        {t.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="overline text-muted-foreground">Имя</label>
                  <input
                    data-testid="booking-name"
                    className="input-editorial mt-2"
                    placeholder="Ваше имя"
                    value={form.name}
                    onChange={(e) => set("name", e.target.value)}
                  />
                </div>
                <div>
                  <label className="overline text-muted-foreground">Телефон</label>
                  <input
                    data-testid="booking-phone"
                    className="input-editorial mt-2"
                    placeholder="+7 ..."
                    value={form.phone}
                    onChange={(e) => set("phone", e.target.value)}
                  />
                </div>
              </div>

              <div>
                <label className="overline text-muted-foreground">Email</label>
                <input
                  data-testid="booking-email"
                  type="email"
                  className="input-editorial mt-2"
                  placeholder="you@mail.com"
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="overline text-muted-foreground">Дата</label>
                  <input
                    data-testid="booking-date"
                    type="date"
                    className="input-editorial mt-2"
                    value={form.date}
                    onChange={(e) => set("date", e.target.value)}
                  />
                </div>
                <div>
                  <label className="overline text-muted-foreground">Кол-во человек</label>
                  <input
                    data-testid="booking-people"
                    type="number"
                    min={1}
                    max={20}
                    className="input-editorial mt-2"
                    value={form.people}
                    onChange={(e) => set("people", e.target.value)}
                  />
                </div>
              </div>

              <div>
                <label className="overline text-muted-foreground">Комментарий (по желанию)</label>
                <textarea
                  data-testid="booking-comment"
                  rows={2}
                  className="input-editorial mt-2 resize-none"
                  placeholder="Пожелания, пересадки, дети..."
                  value={form.comment}
                  onChange={(e) => set("comment", e.target.value)}
                />
              </div>

              <button
                type="submit"
                data-testid="booking-submit"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#F07E26] text-white px-6 py-3.5 text-[14px] font-semibold hover:bg-[#d86a15] disabled:opacity-60 transition"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Отправка...
                  </>
                ) : (
                  "Отправить заявку"
                )}
              </button>
              <p className="text-[11px] text-muted-foreground text-center">
                Нажимая «Отправить», вы соглашаетесь на обработку персональных данных.
              </p>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
